import { collection, doc, getDocs, getFirestore, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const calcularHashSha256 = async (contenido) => {
  const str = cleanText(contenido);
  if (typeof window !== "undefined" && window.crypto && window.crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  
  // Fallback Node.js / Jest environment
  try {
    const req = typeof require !== "undefined" ? require : null;
    if (req) {
      const crypto = req("crypto");
      return crypto.createHash("sha256").update(str, "utf8").digest("hex");
    }
  } catch (e) {
    // Basic hash fallback
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString(16).padStart(64, "0");
  }
};

export const registrarArchivoGeneradoSire = async ({
  rucEmpresa,
  periodo,
  tipo,
  payload,
  propuestaSunatId,
  propuestaOrigen,
  usuario,
}) => {
  const cleanRuc = cleanText(rucEmpresa);
  const cleanPer = cleanText(periodo);
  const cleanTip = cleanText(tipo).toUpperCase();

  const q = query(
    collection(db, CONT_COLLECTIONS.SIRE_ARCHIVOS),
    where("ruc_empresa", "==", cleanRuc),
    where("periodo", "==", cleanPer),
    where("tipo", "==", cleanTip)
  );

  const snap = await getDocs(q);
  let maxVersion = 0;
  snap.docs.forEach((d) => {
    const v = toNum(d.data().version);
    if (v > maxVersion) maxVersion = v;
  });

  const version = maxVersion + 1;
  const versionFormatted = String(version).padStart(3, "0");
  const docId = `${cleanRuc}_${cleanPer}_${cleanTip}_${versionFormatted}`;
  const docRef = doc(db, CONT_COLLECTIONS.SIRE_ARCHIVOS, docId);

  const hash = await calcularHashSha256(payload.contenido || "");

  const dataPayload = {
    ruc_empresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    estado_periodo: "ARCHIVO_GENERADO",
    propuesta_sunat_id: cleanText(propuestaSunatId || `${cleanRuc}_${cleanPer}_${cleanTip}`),
    propuesta_origen: cleanText(propuestaOrigen || "MOCK_SUNAT_SIRE"),
    version,
    nombre_archivo: cleanText(payload.fileName),
    registros: toNum(payload.totalRegistros),
    columnas: cleanTip === "RCE" ? 33 : 34,
    valido: Boolean(payload.prevalidacion?.valido ?? payload.estructura?.ok),
    errores: payload.prevalidacion?.errores || payload.estructura?.invalidRows || [],
    warnings: payload.prevalidacion?.warnings || [],
    hash_sha256: hash,
    contenido: cleanText(payload.contenido),
    generado_at: serverTimestamp(),
    generado_por: cleanText(usuario),
    archivo_generado: true,
  };

  await setDoc(docRef, dataPayload);
  return { docId, version, hash_sha256: hash, ...dataPayload };
};

export const obtenerArchivosGeneradosSire = async ({ rucEmpresa, periodo, tipo }) => {
  const cleanRuc = cleanText(rucEmpresa);
  const cleanPer = cleanText(periodo);
  const cleanTip = cleanText(tipo).toUpperCase();

  const q = query(
    collection(db, CONT_COLLECTIONS.SIRE_ARCHIVOS),
    where("ruc_empresa", "==", cleanRuc),
    where("periodo", "==", cleanPer),
    where("tipo", "==", cleanTip)
  );

  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => toNum(b.version) - toNum(a.version));
};
